import { Alert, Button, Logo } from '@cere/cere-design-system';

interface Props {
  configReady: boolean;
  hasStoredVault: boolean;
  /**
   * True once the boot existence-probe has settled a 404 for the CONNECTED
   * wallet: the server says this wallet has no vault. Combined with
   * `hasStoredVault` below, because a stored record for that same wallet is
   * still a claim marker worth keeping (and still gates the "Use a different
   * wallet" escape) even though there is nothing left to reconnect TO.
   */
  vaultNotFound?: boolean;
  error: string | null;
  onReconnect: () => void;
  onStartClaim: () => void;
  onUseDifferentWallet: () => void;
  /**
   * When set, a "Create new wallet" action starts registration immediately.
   *
   * ONE CLICK, no host-side form. Registration is email → one-time code →
   * passkey and all three steps run in the WALLET ORIGIN's own popup, which is
   * the only surface that can verify a mailbox. This used to reveal a
   * `CreateWalletForm` that collected an email and passed it to
   * `createWallet({ email, name })`; that parameter is gone (the wallet
   * discarded it), so the form collected an address the reader would then have
   * to type again in the popup — possibly differently.
   *
   * Must stay a direct click handler: it opens a popup, and `window.open`
   * outside user activation is blocked.
   */
  onCreateWallet?: () => void;
  /**
   * True while `onCreateWallet`'s registration is outstanding. Expect MINUTES,
   * not seconds: the popup now waits for the reader to fetch a code from their
   * mailbox. It settles on its own if they cancel or close the popup, so this
   * needs no timeout of its own — but the control must stay disabled for the
   * whole time, because a second click opens a second popup.
   */
  creating?: boolean;
  /**
   * True while `onReconnect`'s wallet-connect + Bearer delegation mint is in
   * flight. Mirrors `creating`'s role for the create-wallet path: disables the
   * primary button so a double-click can't fire two connects (and two mints),
   * and swaps the label.
   */
  reconnecting?: boolean;
}

export function VaultConnectPrompt({
  configReady,
  hasStoredVault,
  vaultNotFound = false,
  error,
  onReconnect,
  onStartClaim,
  onUseDifferentWallet,
  onCreateWallet,
  creating = false,
  reconnecting = false,
}: Props) {
  // A stored record alone is not enough to offer "Reconnect wallet": when the
  // connected wallet's own existence probe has 404'd, there is no vault to
  // reconnect to, and `onReconnect` would re-resolve the SAME wallet, re-mint
  // a Bearer delegation and change nothing on screen — a dead primary control
  // that also HIDES `onStartClaim`, the one action that recovers the state.
  // So the primary button follows the probe; only the secondary
  // "Use a different wallet" (and the record itself) follows `hasStoredVault`,
  // because switching wallets is still exactly what a user in this state may
  // want, and that link is its only affordance.
  const offerReconnect = hasStoredVault && !vaultNotFound;
  const primaryBusy = offerReconnect && reconnecting;
  const primaryLabel = offerReconnect ? (primaryBusy ? 'Reconnecting…' : 'Reconnect wallet') : 'Connect wallet';
  const body = offerReconnect
    ? 'Reconnect your wallet to open your saved Vault.'
    : 'Connect your wallet to claim or open your Vault.';
  return (
    <div className="vault-claim">
      <div className="vault-claim__halo vault-claim__halo--tl" aria-hidden />
      <div className="vault-claim__halo vault-claim__halo--br" aria-hidden />
      <div className="vault-claim__card">
        <div className="vault-claim__logo"><Logo size="large" /></div>
        <h1 className="vault-claim__title">Open your Vault</h1>
        <p className="vault-claim__sub">{body}</p>
        <div className="vault-claim__actions">
          <Button
            variant="primary"
            size="large"
            fullWidth
            onClick={offerReconnect ? onReconnect : onStartClaim}
            disabled={!configReady || primaryBusy || creating}
          >
            {primaryLabel}
          </Button>
          {hasStoredVault && (
            <button
              type="button"
              className="vault-claim__textlink"
              onClick={onUseDifferentWallet}
              disabled={!configReady || creating}
            >
              Use a different wallet
            </button>
          )}
          {onCreateWallet && (
            <button
              type="button"
              className="vault-claim__textlink"
              // Wrapped, NOT `onClick={onCreateWallet}`. React passes the
              // click event as the first argument, and `onCreateWallet` is
              // typed `() => void`, so TypeScript accepts the direct form and
              // the callee silently receives a SyntheticBaseEvent. That is how
              // the deleted `{ email, name? }` argument would creep back in —
              // as an object nobody meant to pass, into a `createWallet()` that
              // now takes none. Pinned by VaultConnectPrompt.test.tsx.
              onClick={() => onCreateWallet()}
              disabled={!configReady || creating}
            >
              {creating ? 'Creating…' : 'Create new wallet'}
            </button>
          )}
        </div>
        {!configReady && <div className="vault-claim__hint">Loading configuration…</div>}
      </div>
      {error && (
        <Alert severity="error" sx={{ mt: 2, maxWidth: 440, width: 'min(440px, 92vw)' }}>
          {error}
        </Alert>
      )}
    </div>
  );
}
